import { EmbeddableDatasourceSubplaceWithoutRouter } from '@tableau/data-catalog-client-components';
import { NaturalLanguageDatasourceOwnerPanel } from '@tableau/natural-language-datasource-owner-panel';
import * as React from 'react';
import { defaultMemoize } from 'reselect';
import * as _ from 'underscore';

import { NaturalLanguage } from '~common/react/components/NaturalLanguage';
import { ISanitizer } from '~commonInfra/utils/Sanitize';
import { SessionSiteSelectors } from '~selectors/SessionInfoSelectors';
import { getAppStore } from '~src/app/AppStore';
import { SpacesSiteUrls } from '~src/app/spaces/SpacesSiteUrls';
import { maybeSendTelemetry_favoriteItemOpened } from '~src/app/spaces/SpaceTelemetry';
import {
  explorerSubspace,
  newExtractRefreshButton,
  newRemoteExtractRefreshButton,
} from '~src/app/spaces/SubspaceHelpers';
import { Subspace, TabbedSpace } from '~src/app/spaces/TabbedSpace';
import { ISpaceInfoTypeItemCount } from '~src/app/state/ContentCountsState';
import { DatasourceSubspaceName, SpaceInfo } from '~src/app/state/SpaceInfo';
import { CatalogApiRequests } from '~src/catalog/CatalogApiRequests';
import { catalogApolloClient } from '~src/catalog/CatalogApolloClient';
import { catalogFeatureFlagsAdapter } from '~src/catalog/CatalogFeatureFlagsAdapter';
import { Messages } from '~src/codegen/Localize';
import { ConnectedWorkbooksInDatasourceDefinition } from '~src/contentDefinitions/ConnectedWorkbooksInDatasourceDefinition';
import { DataConnectionDefinition } from '~src/contentDefinitions/DataConnectionDefinition';
import { ExtractTaskDefinition } from '~src/contentDefinitions/ExtractTaskDefinition';
import { RemoteRefreshSchedulesDefinition } from '~src/contentDefinitions/RemoteRefreshSchedulesDefinition';
import { IDataSourceDetailInfoWithUsage } from '~src/datasource/models/IDataSourceInfo';
import { IDatasourceActions } from '~src/datasource/ServerApi/types/IDatasourceActions';
import { Flows } from '~src/flow/resources/Flows';
import { FeatureFlags } from '~src/ts/FeatureFlags';
import { FlowListItemInfo } from '~src/ts/models/FlowInfo';
import { RefreshModeTypes } from '~src/ts/ServerApi/types/IRemoteRefreshProperties';
import { DataCatalogUIActionsService } from '~src/ts/services/DataCatalogUIActionsService';

import { DatasourceSpaceHeader } from './DatasourceSpaceHeader';

export interface DatasourceSpaceStateProps {
  readonly flowsOnSiteEnabled: boolean;
  readonly catalogingOnSiteEnabled: boolean;
  readonly editingFlowsEnabled: boolean;
  readonly spaceInfo: SpaceInfo;
  readonly urls: SpacesSiteUrls;
  readonly contentCounts: ISpaceInfoTypeItemCount<'datasource'>;
  readonly contentCountsUptoDate: boolean;
  readonly siteId: string;
  readonly siteUrlName: string;
  readonly areUsersVisible: boolean;
}

export interface DatasourceSpaceDispatchProps {
  readonly fetchUpdatedContentCounts: () => void;
}

export interface DatasourceSpaceOwnProps {
  readonly datasourceDetail: IDataSourceDetailInfoWithUsage;
  readonly datasourceActions: IDatasourceActions;
  readonly openDetail: () => void;
  readonly addToFavorites: () => void;
  readonly removeFromFavorites: () => void;
  readonly editDatasource: () => void;
  readonly sanitizer: ISanitizer;
  readonly showNLPSubspaces: boolean;
  readonly bridgeEnabled: boolean;
  readonly isExtractRefreshSchedulingAllowedForDatasources: boolean;
}

export type DatasourceSpaceProps = DatasourceSpaceStateProps & DatasourceSpaceDispatchProps & DatasourceSpaceOwnProps;

export const getDatasourceUrl = (urls: SpacesSiteUrls, datasourceId: string, subspace?: DatasourceSubspaceName) => {
  if (subspace) {
    const DatasourceSubspacesToUrl: Record<DatasourceSubspaceName, string> = {
      nlp: urls.datasourceAskDataUrl(datasourceId),
      connections: urls.datasourceConnectionsUrl(datasourceId),
      extractRefreshes: urls.datasourceExtractRefreshesUrl(datasourceId),
      remoteRefreshSchedules: urls.datasourceLocalExtractRefreshesUrl(datasourceId),
      connectedWorkbooks: urls.datasourceConnectedWorkbooksUrl(datasourceId),
      askDataUsageAnalytics: urls.datasourceAskDataUsageAnalyticsUrl(datasourceId),
      lineage: urls.datasourceLineageUrl(datasourceId)
    };

    const url = DatasourceSubspacesToUrl[subspace];
    if (url) {
      return url;
    }
  }

  return urls.datasourceUrl(datasourceId);
};

function nlpSubspace(dataSourceUrlName: string, siteUrlName: string): Subspace {
  return {
    name: 'nlp',
    tabLabel: Messages.nlpTab(),
    shouldRenderTabContentCount: false,
    renderContent: () => {
      return (
        <NaturalLanguage
          dataSourceUrlName={dataSourceUrlName}
          siteUrlName={siteUrlName}
        />
      );
    }
  };
}

function lineageSubspace(
  datasource: IDataSourceDetailInfoWithUsage,
  siteUrls: SpacesSiteUrls,
  areUsersVisible: boolean
): Subspace {
  return {
    name: 'lineage',
    tabLabel: Messages.dataCatalogLineage(),
    shouldRenderTabContentCount: false,
    renderContent: () =>
      <EmbeddableDatasourceSubplaceWithoutRouter
        vizportalUrlId={datasource.id}
        client={catalogApolloClient}
        siteUrls={siteUrls}
        apiRequests={CatalogApiRequests}
        uiActions={DataCatalogUIActionsService.instance()}
        areUsersVisible={areUsersVisible}
        featureFlags={catalogFeatureFlagsAdapter()}
      />
  };
}

export function getDatasourceSubspaces(
  showNLPTab: boolean,
  bridgeEnabled: boolean,
  siteUrlName: string,
  siteId: string,
  datasource: IDataSourceDetailInfoWithUsage,
  datasourceActions: IDatasourceActions,
  contentCounts: ISpaceInfoTypeItemCount<'datasource'>,
  contentCountsUpToDate: boolean,
  siteUrls: SpacesSiteUrls,
  catalogingOnSiteEnabled: boolean,
  areUsersVisible: boolean,
  isExtractRefreshSchedulingAllowedForDatasources: boolean
): ReadonlyArray<Subspace> {
  const showRemoteExtractRefreshes = bridgeEnabled &&
      datasource.hasExtracts &&
      datasource.refreshMode === RefreshModeTypes.Remote;
  const showExtractRefreshes = datasource.hasExtracts && !showRemoteExtractRefreshes;
  const isExtractSSSEnabledForServer = FeatureFlags.selfServiceSchedulesForRefreshEnabled;
  const isExtractSSSEnabledForSite = SessionSiteSelectors.isSelfServiceSchedulesForRefreshEnabled(getAppStore().getState());
  const isExtractSSSEnabled = isExtractSSSEnabledForServer && isExtractSSSEnabledForSite;


  return _.compact([
    showNLPTab && nlpSubspace(datasource.urlName, siteUrlName),
    explorerSubspace({
      name: 'connections',
      tabLabel: Messages.connections(),
      supportedDisplayModes: DataConnectionDefinition.supportedDisplayModes,
      shouldRenderContentCount: true,
      contentCount: contentCounts && contentCounts['connections'],
      contentCountUpToDate: contentCountsUpToDate,
      toolbarVisibility: 'enabled',
      emptyStateProps: {
        primaryMessage: Messages.datasourcesConnectionsEmptyMessage()
      }
    }),
    showExtractRefreshes && explorerSubspace({  // corresponds to /extractRefreshes url
      name: 'extractRefreshes',
      tabLabel: Messages.extractTasks(),
      supportedDisplayModes: ExtractTaskDefinition.supportedDisplayModes,
      toolbarContentSpecificControls: {
        beginning: newExtractRefreshButton(datasource, datasourceActions, !isExtractRefreshSchedulingAllowedForDatasources),
        end: null,
      },
      shouldRenderContentCount: true,
      contentCount: contentCounts && contentCounts['extractRefreshes'],
      contentCountUpToDate: contentCountsUpToDate,
      toolbarVisibility: 'enabled',
      emptyStateProps: {
        primaryMessage: Messages.datasourcesRefreshesEmptyMessage()
      }
    }),
    showRemoteExtractRefreshes && explorerSubspace({  // corresponds to /localExtractRefreshes url
      name: 'remoteRefreshSchedules',
      tabLabel: Messages.extractTasks(),
      supportedDisplayModes: RemoteRefreshSchedulesDefinition.supportedDisplayModes,
      toolbarContentSpecificControls: {
        beginning: isExtractSSSEnabled ? newExtractRefreshButton(datasource, datasourceActions, !isExtractRefreshSchedulingAllowedForDatasources)
          : newRemoteExtractRefreshButton(datasource),
        end: null,
      },
      shouldRenderContentCount: true,
      contentCount: contentCounts && contentCounts['remoteRefreshSchedules'],
      contentCountUpToDate: contentCountsUpToDate,
      toolbarVisibility: 'enabled',
      emptyStateProps: {
        primaryMessage: Messages.datasourcesRefreshesEmptyMessage()
      }
    }),
    explorerSubspace({
      name: 'connectedWorkbooks',
      tabLabel: Messages.connectedWorkbooks(),
      supportedDisplayModes: ConnectedWorkbooksInDatasourceDefinition.supportedDisplayModes,
      shouldRenderContentCount: true,
      contentCount: contentCounts && contentCounts['connectedWorkbooks'],
      contentCountUpToDate: contentCountsUpToDate,
      toolbarVisibility: 'enabled',
      emptyStateProps: {
        primaryMessage: Messages.datasourcesWorkbooksEmptyMessage()
      }
    }),
    catalogingOnSiteEnabled && lineageSubspace(
      datasource,
      siteUrls,
      areUsersVisible
    )
  ]);
}

type DatasourceSpaceState = {
  readonly flows: ReadonlyArray<FlowListItemInfo>;
};

export class DatasourceSpace extends React.PureComponent<DatasourceSpaceProps, DatasourceSpaceState> {

  private readonly memoizedGetDatasourceSubspaces = defaultMemoize(getDatasourceSubspaces);
  private unmounted: boolean = false;

  constructor(props: DatasourceSpaceProps) {
    super(props);
    this.state = {
      flows: [],
    };
    this.fetchFlows();
  }

  public exploreDatasourceUrl = (subspaceName?: DatasourceSubspaceName)  => {
    return getDatasourceUrl(this.props.urls, this.props.datasourceDetail.id, subspaceName);
  }

  public render() {
    const subspaces = this.memoizedGetDatasourceSubspaces(
      this.props.showNLPSubspaces,
      this.props.bridgeEnabled,
      this.props.siteUrlName,
      this.props.siteId,
      this.props.datasourceDetail,
      this.props.datasourceActions,
      this.props.contentCounts,
      this.props.contentCountsUptoDate,
      this.props.urls,
      this.props.catalogingOnSiteEnabled,
      this.props.areUsersVisible,
      this.props.isExtractRefreshSchedulingAllowedForDatasources
    );

    if(this.props.spaceInfo.subspaceName === 'askDataUsageAnalytics' && this.props.showNLPSubspaces) {
      return <NaturalLanguageDatasourceOwnerPanel
        unifiedDatasourceId={this.props.datasourceDetail.idForFavoriting}
        datasourceName={this.props.datasourceDetail.name}
        siteUrlName={this.props.siteUrlName}
      />;
    }

    return (
      <TabbedSpace
        subspaces={subspaces}
        activeSubspaceName={this.props.spaceInfo.subspaceName}
        urlForSubspace={this.exploreDatasourceUrl}
        mastheadElement={this.mastheadElement()}
        testIdPrefix={'datasource'}
        spaceItemName={this.props.datasourceDetail.name}
      />
    );
  }

  public componentDidUpdate(prevProps: DatasourceSpaceProps) {
    if (this.props.datasourceDetail.id !== prevProps.datasourceDetail.id) {
      this.fetchFlows();
      this.maybeSendTelemetryEvent();
    }
    this.fetchUpdatedCounts();
  }

  public componentDidMount(): void {
    this.fetchUpdatedCounts();
    this.maybeSendTelemetryEvent();
  }

  private maybeSendTelemetryEvent() {
    // Use the "ID for favoriting" for data sources so it matches the IDs coming from the favorites menu telemetry.
    // When unified data source IDs are fixed, this can use the datasource's normal ID.
    const datasourceForTelemetry = {
      ...this.props.datasourceDetail,
      id: this.props.datasourceDetail.idForFavoriting,
    };
    maybeSendTelemetry_favoriteItemOpened(datasourceForTelemetry);
  }

  public componentWillUnmount() {
    this.unmounted = true;
  }

  private fetchFlows() {
    if (this.props.flowsOnSiteEnabled) {
      Flows.getFlowsByDatasourceId(this.props.datasourceDetail.id).then(this.updateFlows);
    }
  }

  private readonly updateFlows = (flows: ReadonlyArray<FlowListItemInfo>) => {
    if (!this.unmounted) {
      if (flows && flows.length > 0) {
        this.setState({ flows });
      }
    }
  }

  private mastheadElement(): React.ReactNode {
    return <DatasourceSpaceHeader
      item={this.props.datasourceDetail}
      availableActions={this.props.datasourceActions}
      flows={this.state.flows}
      openDetail={this.props.openDetail}
      addToFavorites={this.props.addToFavorites}
      removeFromFavorites={this.props.removeFromFavorites}
      editDatasource={this.props.editDatasource}
      sanitizer={this.props.sanitizer}
      urls={this.props.urls}
      catalogingOnSiteEnabled={this.props.catalogingOnSiteEnabled}
      editingFlowsEnabled={this.props.editingFlowsEnabled}
    />;
  }

  private fetchUpdatedCounts = (): void => {
    if (this.props.fetchUpdatedContentCounts) {
      this.props.fetchUpdatedContentCounts();
    }
  }
}
